"use client";

import React from "react";
import type { INewsItem } from "../../../interfaces/News.interface";
import VerticalNewsCard from "./VerticalNewsCard";
import BFLApiFailure from "@repo/shared-ui/BFLApiFailure";
import { useHorizontalScroll } from "../hooks/useHorizontalScroll";
import { getCustomerIdForAnalytics } from "../../../utils/analytics-utils";
import {
  campaignName,
  journeyTag,
  productCategory,
  referenceId,
  sprint,
  subSection,
} from "../../../pages/News/newsTrackingConstants";
import { trackFailureScenarioRetryClick } from "./trackFailureScenarioRetry";

interface TrendingNewsClientProps {
  displayNews: INewsItem[];
  scrollLeftAriaLabel?: string;
  scrollRightAriaLabel?: string;
  timestampLabels?: {
    justNow?: string;
    minutesAgo?: string;
    hoursAgo?: string;
    daysAgo?: string;
  };
  images?: {
    defaultSourceLogo?: string;
    defaultNewsBanner?: string;
    errorImage?: string;
    noResultsImage?: string;
  };
}

interface TrendingNewsErrorProps {
  errorTitle?: string;
  errorDescription?: string;
  errorRetryButtonText?: string;
  errorImageAlt?: string;
  errorImageSrc?: string;
  onRetry?: () => void;
}

interface ScrollButtonProps {
  onClick: () => void;
  ariaLabel: string;
  direction: 'left' | 'right';
}

/**
 * Reusable scroll button component
 */
const ScrollButton: React.FC<ScrollButtonProps> = ({ onClick, ariaLabel, direction }) => {
  const baseClasses = "absolute top-1/2 -translate-y-1/2 w-[42px] h-[48px] bg-white/95 border border-[#E4E7EC] shadow-[0_10px_30px_rgba(0,0,0,0.08)] cursor-pointer hidden md:flex items-center justify-center transition-all duration-150 hover:shadow-lg hover:scale-[1.02] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#ff6b35] z-4";
  const leftClasses = "left-0 pt-2 pr-1.5 pb-2 pl-1 rounded-tr-[8px] rounded-br-[8px]";
  const rightClasses = "right-0 pt-2 pr-1 pb-2 pl-1.5 rounded-tl-[8px] rounded-bl-[8px]";
  const positionClasses = direction === 'left' ? leftClasses : rightClasses;
  const arrowPath = direction === 'left' ? "M15 18l-6-6 6-6" : "M9 6l6 6-6 6";

  return (
    <button
      onClick={onClick}
      className={`${baseClasses} ${positionClasses}`}
      aria-label={ariaLabel}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        className="w-6 h-6 text-[#ff6b35]"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d={arrowPath} />
      </svg>
    </button>
  );
};

/**
 * Client component for handling scroll interactions in TrendingNews
 * Handles scroll buttons visibility and scroll behavior
 */
const TrendingNewsClient: React.FC<TrendingNewsClientProps> = ({
  displayNews,
  scrollLeftAriaLabel = "Scroll trending news left",
  scrollRightAriaLabel = "Scroll trending news right",
  timestampLabels,
  images,
}) => {
  const {
    scrollRef,
    showLeftButton,
    showRightButton,
    scrollNext,
    scrollPrev,
  } = useHorizontalScroll([displayNews]);

  return (
    <div className="relative -mx-4 px-4">
      <div className="overflow-hidden">
        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-auto scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          {displayNews.map((item) => (
            <VerticalNewsCard
              key={item.id}
              newsItem={item}
              showCompanies={true}
              showTimestamp={false}
              aemImages={images}
              timestampLabels={timestampLabels}
              newsType="trending"
            />
          ))}
        </div>
      </div>
      {showLeftButton && (
        <ScrollButton
          onClick={scrollPrev}
          ariaLabel={scrollLeftAriaLabel}
          direction="left"
        />
      )}
      {showRightButton && (
        <ScrollButton
          onClick={scrollNext}
          ariaLabel={scrollRightAriaLabel}
          direction="right"
        />
      )}
    </div>
  );
};

/**
 * Client component for error state in TrendingNews
 * Handles error display with retry functionality
 */
export const TrendingNewsError: React.FC<TrendingNewsErrorProps> = ({
  errorTitle = "Unable to fetch the information at this time",
  errorDescription = "Please try again later",
  errorRetryButtonText = "Retry",
  errorImageAlt = "Error",
  errorImageSrc = "",
  onRetry,
}) => {
  return (
    <div className="flex items-center justify-center py-12 min-h-[400px]">
      <BFLApiFailure
        imageSrc={errorImageSrc}
        imageAlt={errorImageAlt}
        error={errorTitle}
        errorDescription={errorDescription}
        retryButtonText={errorRetryButtonText}
        onRetry={onRetry}
        trackingConfig={{
          sectionTitle: `${errorTitle} (Trending news)`,
          componentPosition: "2",
          entryPoint: "no_result_found",
          productCategory,
          subSection,
          journeyTag,
          campaignName,
          sprint,
          referenceId,
        }}
        onRetryClick={(params) =>
          trackFailureScenarioRetryClick(params, getCustomerIdForAnalytics)
        }
      />
    </div>
  );
};

export default TrendingNewsClient;

